import {
  getTrainingEvalBaseline,
  type TrainingEvalBaselineKey,
  type TrainingEvalBaselineView,
  getTrainingEvalRun,
  getTrainingEvalRunMetrics,
  getTrainingEvalRunProgress,
  getTrainingEvalRunSuccessTerms,
  listTrainingEvalRunRecordedVideos,
  listTrainingSessionCheckpoints,
  type TrainingEvalMetricsView,
  type TrainingEvalRunProgressView,
  type TrainingEvalRunView,
  type TrainingEvalSuccessTermsView,
  type TrainingEvalVideoView,
  type TrainingSessionPoliciesView,
} from "@/api/trainingEval";
import { registerSessionCacheReset } from "@/api/sessionCatalogs";
import { evictOldestBeyondCap } from "@/utils/boundedMap";
import { isLiveEvalStatus } from "./evalRunFormat";

// eval-run 상세 데이터의 창 간 공유 캐시 (rules.md §7).
//
// 최소화한 창을 복원하면 소유 훅의 effect 가 다시 실행된다. 여기서 값을 찾으면 그 실행은 요청을
// 보내지 않는다. 컨트롤러 구조를 바꾸지 않고 복원 비용을 없애는 방식이다.
// 상세 창과 비교/병합 창이 같은 run 을 봐도 요청은 한 번이다.
//
// 캐시 가능 판정은 이 모듈이 소유한다. **종료된 run 만 캐시한다.** 진행 중 run 은 응답을 받을 때
// 자기 엔트리를 지운다. 그래서 호출부 분기 없이 캐시가 꺼진다. 진행 중이어도 in-flight promise 는
// 남아 있다. 그래서 폴링 tick 과 동시 조회는 계속 한 요청으로 합쳐진다.
//
// 무효화 API 는 취소(evictEvalRun) 하나다. 종료된 eval-run 은 그 밖에 바뀌지 않는다. 메모는
// 별도 note 엔드포인트가 소유한다.

// entry 당 payload 크기가 달라서 상한을 따로 둔다. success-terms 와 영상 목록은 에피소드 배열을
// 가지므로 상한을 더 작게 둔다.
const RUN_MAX_ENTRIES = 100;
const RESULT_MAX_ENTRIES = 30;
const baselines = new Map<string, Promise<TrainingEvalBaselineView>>();

const runs = new Map<string, Promise<TrainingEvalRunView>>();
const progresses = new Map<string, Promise<TrainingEvalRunProgressView>>();
const metrics = new Map<string, Promise<TrainingEvalMetricsView>>();
const successTerms = new Map<string, Promise<TrainingEvalSuccessTermsView>>();
const recordedVideos = new Map<string, Promise<readonly TrainingEvalVideoView[]>>();
// 학습 중인 세션은 체크포인트가 늘어난다. 이 값을 쓰는 곳은 mlflow 링크 하나뿐이다. 그래서
// 세션 수명 동안 값이 오래되는 것을 허용하고, 상한으로 항목 수만 제한한다.
const sessionCheckpoints = new Map<string, Promise<TrainingSessionPoliciesView>>();

// 캐시 미스면 요청을 시작하고 promise 를 보관한다. 실패한 요청은 남기지 않으므로 다시 열면 재시도한다.
function loadCached<T>(
  cache: Map<string, Promise<T>>,
  key: string,
  maxEntries: number,
  fetcher: () => Promise<T>,
  // 응답을 보고 캐시에 남길지 정한다. 생략하면 항상 남긴다.
  isCacheable?: (value: T) => boolean,
): Promise<T> {
  const cached = cache.get(key);
  if (cached !== undefined) {
    return cached;
  }
  const request = fetcher();
  cache.set(key, request);
  evictOldestBeyondCap(cache, maxEntries);
  request.then(
    (value) => {
      if (cache.get(key) === request && isCacheable !== undefined && !isCacheable(value)) {
        cache.delete(key);
      }
    },
    () => {
      if (cache.get(key) === request) cache.delete(key);
    },
  );
  return request;
}

export function loadEvalBaseline(key: TrainingEvalBaselineKey): Promise<TrainingEvalBaselineView> {
  return loadCached(
    baselines,
    JSON.stringify([key.configName, key.datasetUuid, key.task]),
    RESULT_MAX_ENTRIES,
    () => getTrainingEvalBaseline(key),
  );
}

export function loadEvalRun(evalRunId: string): Promise<TrainingEvalRunView> {
  return loadCached(
    runs,
    evalRunId,
    RUN_MAX_ENTRIES,
    () => getTrainingEvalRun(evalRunId),
    (view) => !isLiveEvalStatus(view.status),
  );
}

export function loadEvalRunProgress(evalRunId: string): Promise<TrainingEvalRunProgressView> {
  return loadCached(
    progresses,
    evalRunId,
    RUN_MAX_ENTRIES,
    () => getTrainingEvalRunProgress(evalRunId),
    (view) => !isLiveEvalStatus(view.status),
  );
}

// metrics·success-terms·영상은 succeeded run 에만 요청되므로 응답 판정 없이 캐시한다.
export function loadEvalRunMetrics(evalRunId: string): Promise<TrainingEvalMetricsView> {
  return loadCached(metrics, evalRunId, RESULT_MAX_ENTRIES, () =>
    getTrainingEvalRunMetrics(evalRunId),
  );
}

export function loadEvalRunSuccessTerms(evalRunId: string): Promise<TrainingEvalSuccessTermsView> {
  return loadCached(successTerms, evalRunId, RESULT_MAX_ENTRIES, () =>
    getTrainingEvalRunSuccessTerms(evalRunId),
  );
}

export function loadEvalRunRecordedVideos(
  evalRunId: string,
): Promise<readonly TrainingEvalVideoView[]> {
  return loadCached(recordedVideos, evalRunId, RESULT_MAX_ENTRIES, () =>
    listTrainingEvalRunRecordedVideos(evalRunId),
  );
}

export function loadSessionCheckpoints(sessionId: string): Promise<TrainingSessionPoliciesView> {
  return loadCached(sessionCheckpoints, sessionId, RESULT_MAX_ENTRIES, () =>
    listTrainingSessionCheckpoints(sessionId),
  );
}

// 취소처럼 종단 run 을 바꾸는 mutation 뒤에 호출한다.
export function evictEvalRun(evalRunId: string): void {
  runs.delete(evalRunId);
  progresses.delete(evalRunId);
  metrics.delete(evalRunId);
  successTerms.delete(evalRunId);
  recordedVideos.delete(evalRunId);
}

// 세션 경계에서 호출한다. 사용자 전환 시 이전 사용자의 run 결과가 남지 않아야 한다.
export function clearEvalRunDataCache(): void {
  baselines.clear();
  runs.clear();
  progresses.clear();
  metrics.clear();
  successTerms.clear();
  recordedVideos.clear();
  sessionCheckpoints.clear();
}

registerSessionCacheReset(clearEvalRunDataCache);
